/* eslint-disable @typescript-eslint/no-explicit-any */
﻿import { SlateEntities, SlateKpiItem, SlateMetricFocus } from './slate.types';

/** Build only the KPIs the user asked for from film analytics payload */
export function selectFilmAnalyticsKpis(
    data: any,
    e: SlateEntities,
    opts: { filmmaker: boolean; currency?: string }
): { kpis: SlateKpiItem[]; title: string; chips: string[]; body: string } {
    const currency = opts.currency || '₹';
    const focus = resolveFocus(e, opts.filmmaker);
    const all = buildAllFilmKpis(data, currency);
    const chips: string[] = [];

    const pick = (...keys: string[]): SlateKpiItem[] =>
        keys
            .map((k) => all.find((x) => x.key === k))
            .filter(Boolean)
            .map((x) => ({ label: x!.label, value: x!.value }));

    let kpis: SlateKpiItem[] = [];
    let title = opts.filmmaker ? 'Your film metrics' : 'Film metrics';
    let body = 'Showing only metrics you asked for.';

    switch (focus) {
        case 'views':
            title = 'Film views';
            kpis = pick('totalViews');
            chips.push('Show unique views', 'Show all metrics', 'Top 5 films');
            break;
        case 'unique_views':
            title = 'Unique views';
            kpis = pick('uniqueViews');
            chips.push('Show views', 'Show all metrics');
            break;
        case 'completed_views':
            title = 'Completed views';
            kpis = pick('completedViews');
            chips.push('Show incomplete views', 'Show all metrics');
            break;
        case 'incomplete_views':
            title = 'Incomplete views';
            kpis = pick('incompleteViews');
            chips.push('Show completed views', 'Show all metrics');
            break;
        case 'minutes':
            title = 'Minutes watched';
            kpis = pick('minutesWatched');
            chips.push('Show views', 'Show all metrics');
            break;
        case 'purchases':
            title = 'Purchases';
            kpis = pick('totalPurchases', 'purchases', 'promoPurchases');
            chips.push('Show revenue', 'Show all metrics');
            break;
        case 'revenue':
            title = 'Revenue';
            kpis = opts.filmmaker
                ? pick('filmmakerRevenue')
                : pick('filmmakerRevenue', 'boxOfficeRevenue');
            if (opts.filmmaker) {
                chips.push('Show views', 'Show purchases', 'Show all metrics');
            } else {
                chips.push(
                    'Show filmmaker revenue',
                    'Show box office revenue',
                    'Show purchases',
                    'Show all metrics'
                );
            }
            break;
        case 'filmmaker_revenue':
            title = opts.filmmaker ? 'My revenue' : 'Filmmaker revenue';
            kpis = pick('filmmakerRevenue');
            chips.push(
                ...(opts.filmmaker
                    ? (['Show views', 'Show all metrics'] as string[])
                    : ([
                          'Show box office revenue',
                          'Show revenue',
                          'Show all metrics',
                      ] as string[]))
            );
            break;
        case 'boxoffice_revenue':
            title = 'Box office revenue';
            kpis = pick('boxOfficeRevenue');
            chips.push(
                'Show filmmaker revenue',
                'Show revenue',
                'Show all metrics'
            );
            break;
        case 'all':
            title = 'Full film analytics';
            kpis = all.map(({ label, value }) => ({ label, value }));
            body = 'Full metric set for this range.';
            chips.push('Top 5 films', 'Show revenue', 'Show views');
            break;
        case 'summary':
        default:
            title = 'Film snapshot';
            kpis = opts.filmmaker
                ? pick('totalViews', 'filmmakerRevenue')
                : pick('totalViews', 'filmmakerRevenue', 'boxOfficeRevenue');
            body =
                'Compact snapshot — ask for a metric or tap a chip for more.';
            chips.push(
                'Show all metrics',
                'Show revenue',
                'Show views',
                'Top 5 films'
            );
            break;
    }

    const seen = new Set<string>();
    const uniqChips = chips.filter((c) => {
        if (seen.has(c)) return false;
        seen.add(c);
        return true;
    });

    return { kpis, title, chips: uniqChips, body };
}

/** Web series — same progressive-disclosure rules (views / unique / minutes) */
export function selectWebSeriesAnalyticsKpis(
    data: any,
    e: SlateEntities,
    opts: { filmmaker: boolean }
): { kpis: SlateKpiItem[]; title: string; chips: string[]; body: string } {
    const focus = resolveFocus(e, opts.filmmaker);
    const all: Array<SlateKpiItem & { key: string }> = [
        { key: 'totalViews', label: 'Total views', value: data.totalViews ?? 0 },
        {
            key: 'uniqueViews',
            label: 'Unique views',
            value: data.totalUniqueViews ?? 0,
        },
        {
            key: 'minutesWatched',
            label: 'Minutes watched',
            value: data.totalMinutesWatched ?? 0,
        },
    ];
    const pick = (...keys: string[]) =>
        keys
            .map((k) => all.find((x) => x.key === k))
            .filter(Boolean)
            .map((x) => ({ label: x!.label, value: x!.value }));

    let kpis: SlateKpiItem[] = [];
    let title = opts.filmmaker ? 'Your series metrics' : 'Series metrics';
    let body = 'Showing only metrics you asked for.';
    const chips: string[] = [];

    switch (focus) {
        case 'views':
            title = 'Series views';
            kpis = pick('totalViews');
            chips.push('Show unique views', 'Show all metrics', 'Top 5 seasons');
            break;
        case 'unique_views':
            title = 'Unique views';
            kpis = pick('uniqueViews');
            chips.push('Show views', 'Show all metrics');
            break;
        case 'minutes':
            title = 'Minutes watched';
            kpis = pick('minutesWatched');
            chips.push('Show views', 'Show all metrics');
            break;
        case 'all':
            title = 'Full series analytics';
            kpis = all.map(({ label, value }) => ({ label, value }));
            body = 'Full metric set for this range.';
            chips.push('Top 5 seasons');
            break;
        case 'summary':
        default:
            // Revenue/purchases not in series payload — fall back to compact views
            title = 'Series snapshot';
            kpis = pick('totalViews', 'uniqueViews');
            body =
                'Compact snapshot — ask for a metric or tap a chip for more.';
            chips.push('Show all metrics', 'Show views', 'Top 5 seasons');
            break;
    }

    const seen = new Set<string>();
    return {
        kpis,
        title,
        body,
        chips: chips.filter((c) => {
            if (seen.has(c)) return false;
            seen.add(c);
            return true;
        }),
    };
}

/** Top table only when user asked for ranking / top-N — never dump with metrics */
export function shouldShowTopFilmsTable(e: SlateEntities): boolean {
    return !!e.wantTopTable;
}

function resolveFocus(e: SlateEntities, filmmaker: boolean): SlateMetricFocus {
    if (e.wantAllMetrics) return 'all';
    if (e.metricFocus) {
        if (filmmaker && e.metricFocus === 'revenue') return 'filmmaker_revenue';
        if (filmmaker && e.metricFocus === 'boxoffice_revenue') {
            // Filmmaker role: no box-office KPI — treat as own revenue
            return 'filmmaker_revenue';
        }
        return e.metricFocus;
    }
    return 'summary';
}

function buildAllFilmKpis(
    data: any,
    currency: string
): Array<SlateKpiItem & { key: string }> {
    const money = (n: number) =>
        `${currency}${Number(n || 0).toLocaleString('en-IN')}`;
    return [
        { key: 'totalViews', label: 'Total views', value: data.totalViews ?? 0 },
        {
            key: 'uniqueViews',
            label: 'Unique views',
            value: data.totalUniqueViews ?? 0,
        },
        {
            key: 'completedViews',
            label: 'Completed views',
            value: data.totalCompletedViews ?? 0,
        },
        {
            key: 'incompleteViews',
            label: 'Incomplete views',
            value: data.totalIncompleteViews ?? 0,
        },
        {
            key: 'minutesWatched',
            label: 'Minutes watched',
            value: data.totalMinutesWatched ?? 0,
        },
        {
            key: 'totalPurchases',
            label: 'Total purchases',
            value: data.totalPurchases ?? 0,
        },
        {
            key: 'purchases',
            label: 'Purchases',
            value: data.Purchases ?? data.purchases ?? 0,
        },
        {
            key: 'promoPurchases',
            label: 'Promotional purchases',
            value: data.promotionalPurchases ?? 0,
        },
        {
            key: 'filmmakerRevenue',
            label: 'Filmmaker revenue',
            value: money(data.totalEarnings ?? 0),
        },
        {
            key: 'boxOfficeRevenue',
            label: 'Box office revenue',
            value: money(data.totalCollection ?? 0),
        },
    ];
}

/** User analytics — only asked KPIs */
export function selectUserAnalyticsKpis(
    data: any,
    e: SlateEntities
): { kpis: SlateKpiItem[]; title: string; chips: string[]; body: string } {
    const active =
        data.totalActiveUsers ?? data.totalUsers ?? data.activeUsers ?? 0;
    const neu = data.totalNewUsers ?? data.newUsers ?? 0;
    const all = [
        { key: 'active', label: 'Active users', value: active },
        { key: 'new', label: 'New users', value: neu },
    ];
    const pick = (...keys: string[]) =>
        keys
            .map((k) => all.find((x) => x.key === k))
            .filter(Boolean)
            .map((x) => ({ label: x!.label, value: x!.value }));

    const focus = e.wantAllMetrics ? 'all' : e.metricFocus || 'summary';
    if (focus === 'new_users') {
        return {
            title: 'New users',
            body: 'Showing only metrics you asked for.',
            kpis: pick('new'),
            chips: ['Show active users', 'Show all metrics'],
        };
    }
    if (focus === 'active_users') {
        return {
            title: 'Active users',
            body: 'Showing only metrics you asked for.',
            kpis: pick('active'),
            chips: ['Show new users', 'Show all metrics'],
        };
    }
    if (focus === 'all') {
        return {
            title: 'Full user analytics',
            body: 'Full metric set for this range.',
            kpis: all.map(({ label, value }) => ({ label, value })),
            chips: ['Show new users', 'Show active users'],
        };
    }
    return {
        title: 'User snapshot',
        body: 'Compact snapshot — tap a chip for more.',
        kpis: pick('active', 'new'),
        chips: ['Show all metrics', 'Show new users', 'Show active users'],
    };
}

/** Film statistics — status / focus aware, no raw Object.entries dump */
export function selectFilmStatsKpis(
    data: any,
    e: SlateEntities,
    filmmaker: boolean
): { kpis: SlateKpiItem[]; title: string; chips: string[]; body: string } {
    const entries = Object.entries(data || {})
        .filter(
            ([, v]) =>
                typeof v === 'number' ||
                (typeof v === 'string' && !isNaN(Number(v)))
        )
        .map(([k, v]) => ({
            key: k,
            label: k
                .replace(/([A-Z])/g, ' $1')
                .replace(/[_-]+/g, ' ')
                .replace(/\s+/g, ' ')
                .trim(),
            value: v as string | number,
        }));

    const status = (e.status || '').toLowerCase();
    const wantAll = e.wantAllMetrics || e.metricFocus === 'all';

    let filtered = entries;
    if (!wantAll && status) {
        const hit = entries.filter((x) =>
            x.key.toLowerCase().includes(status.toLowerCase())
        );
        if (hit.length) filtered = hit;
    } else if (!wantAll && entries.length > 3) {
        // Compact snapshot: prefer common status keys, else first 3
        const prefer = [
            'pending',
            'approved',
            'rejected',
            'draft',
            'total',
            'published',
        ];
        const preferred = entries.filter((x) =>
            prefer.some((p) => x.key.toLowerCase().includes(p))
        );
        filtered = (preferred.length ? preferred : entries).slice(0, 3);
    }

    const chips = ['Show all metrics'];
    if (status !== 'PENDING') chips.push('How many films pending?');
    if (status !== 'APPROVED') chips.push('How many films approved?');

    return {
        title: filmmaker ? 'Your film statistics' : 'Film statistics',
        body: wantAll
            ? 'Full statistic set.'
            : 'Showing only metrics you asked for.',
        kpis: filtered.map(({ label, value }) => ({ label, value })),
        chips: [...new Set(chips)],
    };
}

/** Wallet — cash / reel / level only when asked */
export function selectWalletKpis(
    data: any,
    e: SlateEntities
): { kpis: SlateKpiItem[]; title: string; chips: string[]; body: string } {
    const cash = data.walletBalance?.totalBalance ?? 0;
    const reel = data.reelBalance?.totalBalance ?? 0;
    const level =
        data.level?.currentLevel?.name ||
        data.level?.currentLevel?.level ||
        '—';
    const all = [
        { key: 'cash', label: 'Cash balance', value: cash },
        { key: 'reel', label: 'Reel balance', value: reel },
        { key: 'level', label: 'Level', value: level },
    ];
    const pick = (...keys: string[]) =>
        keys
            .map((k) => all.find((x) => x.key === k))
            .filter(Boolean)
            .map((x) => ({ label: x!.label, value: x!.value }));

    const focus = e.wantAllMetrics ? 'all' : e.metricFocus || 'summary';
    if (focus === 'cash') {
        return {
            title: 'Cash balance',
            body: 'Showing only metrics you asked for.',
            kpis: pick('cash'),
            chips: ['Show reel balance', 'Show all metrics'],
        };
    }
    if (focus === 'reel') {
        return {
            title: 'Reel balance',
            body: 'Showing only metrics you asked for.',
            kpis: pick('reel'),
            chips: ['Show cash balance', 'Show all metrics'],
        };
    }
    if (focus === 'level') {
        return {
            title: 'Filmmaker level',
            body: 'Showing only metrics you asked for.',
            kpis: pick('level'),
            chips: ['Show cash balance', 'Show all metrics'],
        };
    }
    if (focus === 'all') {
        return {
            title: 'Wallet & level',
            body: 'Full wallet snapshot.',
            kpis: all.map(({ label, value }) => ({ label, value })),
            chips: ['Show cash balance', 'Show reel balance'],
        };
    }
    return {
        title: 'Wallet snapshot',
        body: 'Compact snapshot — tap a chip for more.',
        kpis: pick('cash', 'reel'),
        chips: ['Show level', 'Show all metrics', 'Show cash balance'],
    };
}
